import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Why Us", to: "/why-us" },
  { label: "Super Herbs", to: "/herbs" },
  { label: "Certifications", to: "/certifications" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? "text-[#C05C46]" : "text-[#1c2826]/80 hover:text-[#1A4331]"
  }`;

export const Header = () => {
  const { count, setCartOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-[#E5E0D8] bg-[#F9F6F0]/85 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img
              src="/logo.png"
              alt="11 Star — Wellness for Life"
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          <nav data-testid="main-nav" className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              data-testid="cart-open-button"
              variant="outline"
              className="relative rounded-full border-[#1A4331]/30 text-[#1A4331] hover:bg-[#1A4331] hover:text-white transition-colors"
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Cart
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#C05C46] text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Button>
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 text-[#1A4331]"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E5E0D8] bg-[#F9F6F0]">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium py-1 ${isActive ? "text-[#C05C46]" : "text-[#1c2826]"}`
                }
                onClick={() => setMenuOpen(false)}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
